import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import { useNavigate } from "react-router-dom";
import whiteBjsLogo from "../../../images/WhiteBJSEnglish1.svg";
import profilePhoto from "../../../images/BJSPhoto.png";

const scheduledEvents = [
  {
    id: 1,
    name: "Smart Girl Raichur Workshop",
    date: "2026-04-15",
    mode: "Offline",
  },
  {
    id: 2,
    name: "Smart Girl Jahajpur Workshop",
    date: "2026-03-20",
    mode: "Offline",
  },
  {
    id: 3,
    name: "Smart Girl Delhi Workshop",
    date: "2026-02-05",
    mode: "Offline",
  },
  {
    id: 4,
    name: "Smart Girl Webinar",
    date: "2026-01-10",
    mode: "Online",
  },
];

const sortOptions = {
  newest: "Date Modified",
  oldest: "Oldest First",
  name: "Event Name",
};

const formatDate = (dateValue) => {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const ProgramScheduledEvents  = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const filteredEvents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const nextEvents = scheduledEvents.filter((event) => {
      if (!normalizedSearch) {
        return true;
      }

      return [event.name, event.mode]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    });

    return [...nextEvents].sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      }

      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      if (sortBy === "oldest") {
        return dateA - dateB;
      }

      return dateB - dateA;
    });
  }, [searchTerm, sortBy]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fff", color: "#231f20" }}>
   

      <Box sx={{ px: { xs: 2, md: 6, lg: 10 }, py: { xs: 3, md: 6 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "center" },
            justifyContent: "space-between",
            gap: 3,
            mb: 5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                border: "1px solid #d0d0d0",
                color: "#b5b5b5",
                mt: 0.5,
              }}
            >
              <UndoRoundedIcon />
            </IconButton>

            <Box>
              <h1
                style={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: "-0.03em",
                }}
              >
                Program Scheduled
              </h1>
              <h5 style={{ mt: 0.5, fontSize: { xs: 18, md: 22 }, color: "#312d2f" }}>
                Workshops, Events And Webinars
              </h5>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <TextField
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search"
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 320, md: 405 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  height: 48,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: "#7b7b7b" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 230 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  height: 48,
                },
              }}
              SelectProps={{
                IconComponent: KeyboardArrowDownRoundedIcon,
                displayEmpty: true,
                renderValue: (value) => (
                  <Box component="span" sx={{ color: "#4c4c4c" }}>
                    Sort By: <Box component="span" sx={{ fontWeight: 600, color: "#1e1e1e" }}>{sortOptions[value]}</Box>
                  </Box>
                ),
              }}
            >
              {Object.entries(sortOptions).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px solid #9f9f9f",
            borderRadius: "14px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "2fr 1.2fr 1fr", md: "2.5fr 1.4fr 1fr 1fr" },
              gap: 2,
              px: { xs: 2, md: 4 },
              py: 2,
              background: "linear-gradient(90deg, #fff6f4 0%, #fff1ed 100%)",
              borderBottom: "1px solid #a7a7a7",
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#6d6666", textTransform: "uppercase" }}>
              Event Name
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#6d6666", textTransform: "uppercase" }}>
              Date
            </Typography>
            <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: 15, fontWeight: 500, color: "#6d6666", textTransform: "uppercase" }}>
              Mode
            </Typography>
            <Typography sx={{ textAlign: { xs: "right", md: "left" }, fontSize: 15, fontWeight: 500, color: "#6d6666", textTransform: "uppercase" }}>
              Action
            </Typography>
          </Box>

          {filteredEvents.map((event) => (
            <Box
              key={event.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "2fr 1.2fr 1fr", md: "2.5fr 1.4fr 1fr 1fr" },
                gap: 2,
                alignItems: "center",
                px: { xs: 2, md: 4 },
                py: { xs: 2, md: 2.25 },
                borderBottom: "1px solid #a7a7a7",
              }}
            >
              <Typography sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 700, lineHeight: 1.25 }}>
                {event.name}
              </Typography>
              <Typography sx={{ fontSize: { xs: 17, md: 20 }, color: "#3d3b3d" }}>
                {formatDate(event.date)}
              </Typography>
              <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: { md: 20 }, color: "#3d3b3d" }}>
                {event.mode}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: { xs: "flex-end", md: "flex-start" } }}>
                <Button
                  variant="contained"
                  disableElevation
                  sx={{
                    minWidth: 100,
                    borderRadius: "10px",
                    backgroundColor: "#ee866f",
                    textTransform: "none",
                    fontSize: 18,
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.75,
                    "&:hover": {
                      backgroundColor: "#e2775f",
                    },
                  }}
                >
                  Enquiry
                </Button>
              </Box>
            </Box>
          ))}

          {!filteredEvents.length && (
            <Box sx={{ px: 4, py: 7, textAlign: "center" }}>
              <Typography sx={{ fontSize: 20, fontWeight: 600 }}>No scheduled events found</Typography>
              <Typography sx={{ mt: 1, color: "#6b6b6b" }}>
                Try a different search or sort option.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProgramScheduledEvents ;






// import React, { useEffect, useMemo, useState } from "react";
// import {
//   Box,
//   Button,
//   IconButton,
//   InputAdornment,
//   MenuItem,
//   TextField,
//   Typography,
// } from "@mui/material";
// import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
// import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
// import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
// import { useNavigate, useParams } from "react-router-dom";
// import ApiCallService from "../../../Service/ApiCallService";
// import Spinner from "../../../components/common/Spinner";

// const sortOptions = {
//   newest: "Date Modified",
//   oldest: "Oldest First",
//   name: "Event Name",
// };

// const formatDate = (dateValue) => {
//   const date = new Date(dateValue);

//   if (Number.isNaN(date.getTime())) return "";

//   return date.toLocaleDateString("en-US", {
//     month: "long",
//     day: "numeric",
//     year: "numeric",
//   });
// };

// const ProgramScheduledEvents = () => {
//   const navigate = useNavigate();
//   const { id } = useParams();

//   const [events, setEvents] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [searchTerm, setSearchTerm] = useState("");
//   const [sortBy, setSortBy] = useState("newest");

//   // 🔥 FETCH EVENTS BY PROGRAM ID
//   useEffect(() => {
//     if (!id) return;

//     const fetchEvents = async () => {
//       try {
//         setLoading(true);

//         const res = await ApiCallService.get(
//           `${process.env.REACT_APP_BASE_URL}/calendarbooking/program/${id}`
//         );

//         setEvents(res?.data || []);
//       } catch (err) {
//         console.error("Error fetching program events:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchEvents();
//   }, [id]);

//   // 🔍 FILTER + SORT
//   const filteredEvents = useMemo(() => {
//     const normalizedSearch = searchTerm.trim().toLowerCase();

//     const filtered = events.filter((event) => {
//       if (!normalizedSearch) return true;

//       return (
//         event.heading?.toLowerCase().includes(normalizedSearch) ||
//         event.meetingType?.toLowerCase().includes(normalizedSearch) ||
//         event.programId?.name?.toLowerCase().includes(normalizedSearch)
//       );
//     });

//     return [...filtered].sort((a, b) => {
//       if (sortBy === "name") {
//         return (a.heading || "").localeCompare(b.heading || "");
//       }

//       const dateA = new Date(a.startDate).getTime();
//       const dateB = new Date(b.startDate).getTime();

//       if (sortBy === "oldest") {
//         return dateA - dateB;
//       }

//       return dateB - dateA;
//     });
//   }, [events, searchTerm, sortBy]);

//   return (
//     <Box sx={{ minHeight: "100vh", backgroundColor: "#fff", color: "#231f20" }}>
//       <Spinner loading={loading} />

//       <Box sx={{ px: { xs: 2, md: 6, lg: 10 }, py: { xs: 3, md: 6 } }}>
//         {/* HEADER */}
//         <Box
//           sx={{
//             display: "flex",
//             flexDirection: { xs: "column", lg: "row" },
//             alignItems: { xs: "stretch", lg: "center" },
//             justifyContent: "space-between",
//             gap: 3,
//             mb: 5,
//           }}
//         >
//           <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
//             <IconButton
//               onClick={() => navigate(-1)}
//               sx={{
//                 border: "1px solid #d0d0d0",
//                 color: "#b5b5b5",
//                 mt: 0.5,
//               }}
//             >
//               <UndoRoundedIcon />
//             </IconButton>

//             <Box>
//               <Typography
//                 sx={{
//                   fontSize: { xs: 28, md: 42 },
//                   fontWeight: 700,
//                   lineHeight: 1.1,
//                 }}
//               >
//                 Program Scheduled
//               </Typography>
//               <Typography sx={{ mt: 0.5, fontSize: { xs: 18, md: 22 } }}>
//                 Workshops, Events And Webinars
//               </Typography>
//             </Box>
//           </Box>

//           {/* SEARCH + SORT */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: { xs: "column", sm: "row" },
//               gap: 2,
//             }}
//           >
//             <TextField
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               placeholder="Search"
//               size="small"
//               sx={{
//                 minWidth: { xs: "100%", sm: 320 },
//                 "& .MuiOutlinedInput-root": {
//                   borderRadius: "14px",
//                   height: 48,
//                 },
//               }}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchRoundedIcon />
//                   </InputAdornment>
//                 ),
//               }}
//             />

//             <TextField
//               select
//               size="small"
//               value={sortBy}
//               onChange={(e) => setSortBy(e.target.value)}
//               sx={{
//                 minWidth: { xs: "100%", sm: 230 },
//                 "& .MuiOutlinedInput-root": {
//                   borderRadius: "14px",
//                   height: 48,
//                 },
//               }}
//               SelectProps={{
//                 IconComponent: KeyboardArrowDownRoundedIcon,
//                 renderValue: (value) => (
//                   <span>
//                     Sort By: <b>{sortOptions[value]}</b>
//                   </span>
//                 ),
//               }}
//             >
//               {Object.entries(sortOptions).map(([value, label]) => (
//                 <MenuItem key={value} value={value}>
//                   {label}
//                 </MenuItem>
//               ))}
//             </TextField>
//           </Box>
//         </Box>

//         {/* TABLE */}
//         <Box
//           sx={{
//             border: "1px solid #9f9f9f",
//             borderRadius: "14px",
//             overflow: "hidden",
//           }}
//         >
//           {/* HEADER ROW */}
//           <Box
//             sx={{
//               display: "grid",
//               gridTemplateColumns: {
//                 xs: "2fr 1.2fr 1fr",
//                 md: "2.5fr 1.4fr 1fr 1fr",
//               },
//               px: 4,
//               py: 2,
//               background: "#fff1ed",
//               borderBottom: "1px solid #a7a7a7",
//             }}
//           >
//             <Typography>Event Name</Typography>
//             <Typography>Date</Typography>
//             <Typography sx={{ display: { xs: "none", md: "block" } }}>
//               Mode
//             </Typography>
//             <Typography>Action</Typography>
//           </Box>

//           {/* DATA ROWS */}
//           {filteredEvents.map((event) => (
//             <Box
//               key={event._id}
//               sx={{
//                 display: "grid",
//                 gridTemplateColumns: {
//                   xs: "2fr 1.2fr 1fr",
//                   md: "2.5fr 1.4fr 1fr 1fr",
//                 },
//                 px: 4,
//                 py: 2,
//                 borderBottom: "1px solid #a7a7a7",
//               }}
//             >
//               <Typography sx={{ fontWeight: 700 }}>
//                 {event.heading}
//               </Typography>

//               <Typography>
//                 {formatDate(event.startDate)}
//               </Typography>

//               <Typography sx={{ display: { xs: "none", md: "block" } }}>
//                 {event.meetingType}
//               </Typography>

//               <Button
//                 variant="contained"
//                 sx={{
//                   backgroundColor: "#ee866f",
//                   textTransform: "none",
//                 }}
//                 onClick={() =>
//                   navigate(`/event-details/${event._id}`)
//                 }
//               >
//                 View
//               </Button>
//             </Box>
//           ))}

//           {/* EMPTY STATE */}
//           {!loading && filteredEvents.length === 0 && (
//             <Box sx={{ px: 4, py: 6, textAlign: "center" }}>
//               <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
//                 No scheduled events found
//               </Typography>
//             </Box>
//           )}
//         </Box>
//       </Box>
//     </Box>
//   );
// };

// export default ProgramScheduledEvents;